import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { ObjectPassportFieldsPayload } from '@/entities/Inventory/ObjectPassportFields'
import {
  isObjectPassportCatalogField,
  isObjectPassportReferenceField,
  objectPassportValueForField,
  objectPassportValueToEditScalar,
  objectPassportValueToRuntimeScalar,
} from '@/entities/Inventory/ObjectPassportFields/model/objectPassportFieldAdapter'
import { isElementLinkedFieldCell } from '@/features/Inventory/ObjectTypePassportGrid/model/passportFieldPalette'
import {
  focusFieldIdForPlaceholder,
  getFieldMetaForPlaceholder,
} from './passportRuntimeFieldDisplay'
import {
  parseHopSegmentsForLinkedResolution,
  parseLeafFieldIdForLinkedResolution,
  parseLinkFieldIdFromElementId,
  parseRefHopChainFromGridId,
} from './passportRefGridLinkedParse'
import type { RuntimeFieldSlot, RuntimePlaceholderItem } from './runtimeLayoutAdapter'

export type RuntimeFieldModalTarget = {
  fieldId: string
  field: ObjectInventoryField
  label: string
}

/** Relation-backed link_object fields can open the selected element in a new tab; list/list_types cannot. */
export function fieldHasFollowableRelation(
  field: ObjectInventoryField | null | undefined,
): boolean {
  if (!field) return false
  const type = String(field.type ?? '').trim()
  if (type === 'list' || type === 'list_types') return false
  if (Number(field.relation_field_id) > 0) return true
  return (type === 'link_object' || type === 'object_link') && Number(field.link_target_type_id) > 0
}

/**
 * Нормализует поле связи для модалки: ObjectLinkSelect ждёт целевой тип в `object_type`.
 * Для link-полей приоритет у `link_target_type_id` (как `reference_id` в нормализованном
 * адаптере) — legacy object-types API может отдавать некорректный `object_type`.
 */
export const resolveModalLinkFieldType = (
  field: ObjectInventoryField,
): ObjectInventoryField => {
  const type = String(field.type ?? '')
  if (type !== 'link_object' && type !== 'object_link') return field
  const linkTargetTypeId = Number(field.link_target_type_id)
  if (!Number.isFinite(linkTargetTypeId) || linkTargetTypeId <= 0) return field
  if (Number(field.object_type) === linkTargetTypeId) return field
  return { ...field, object_type: linkTargetTypeId }
}

export function parseSelectedObjectListId(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Находит поле, в котором backend фактически вернул ID связи для reference-карточки.
 * Fallback применяется только при единственном заполненном relation-поле, чтобы не спутать связи.
 */
export const resolveReferenceValueSourceFieldId = (
  fieldId: string,
  payload: ObjectPassportFieldsPayload | null | undefined,
): string => {
  if (!payload) return fieldId

  const numericFieldId = Number(fieldId)
  const field = payload.fields.find((item) => item.id === numericFieldId)
  if (!field || !isObjectPassportReferenceField(field)) return fieldId

  const directValue = objectPassportValueForField(numericFieldId, payload.values)
  if (directValue && objectPassportValueToEditScalar(directValue) !== '') return fieldId

  const definitionsById = new Map(payload.fields.map((item) => [item.id, item]))
  const relationSources = payload.values.filter((value) => {
    if (objectPassportValueToEditScalar(value) === '') return false
    const definition = definitionsById.get(value.field_id)
    return Number(definition?.validation_props?.relation_field_id) > 0
  })

  return relationSources.length === 1 ? String(relationSources[0].field_id) : fieldId
}

/** Подставляет фактическое relation-поле значения, сохраняя метаданные исходного reference. */
export const resolveFieldModalValueSource = (
  target: RuntimeFieldModalTarget,
  payload: ObjectPassportFieldsPayload | null | undefined,
): RuntimeFieldModalTarget => ({
  ...target,
  fieldId: resolveReferenceValueSourceFieldId(target.fieldId, payload),
})

/** Возвращает подпись текущего catalog-значения; для reference всегда оставляет загрузку имени списку. */
export const resolveCatalogSelectDisplayValue = (
  fieldId: string | number | null | undefined,
  payload: ObjectPassportFieldsPayload | null | undefined,
): string => {
  const numericFieldId = Number(fieldId)
  if (!Number.isFinite(numericFieldId) || numericFieldId <= 0 || !payload) return ''

  const definition = payload.fields.find((field) => field.id === numericFieldId)
  if (!definition || !isObjectPassportCatalogField(definition)) return ''

  const storedValue = objectPassportValueForField(numericFieldId, payload.values)
  const valueDisplay = storedValue ? objectPassportValueToRuntimeScalar(storedValue).trim() : ''
  if (valueDisplay && valueDisplay !== storedValue?.value) return valueDisplay

  return (
    definition.field_props
      .find((fieldProps) => fieldProps.value_result?.trim())
      ?.value_result?.trim() ?? ''
  )
}

const findFieldById = (
  fields: ObjectInventoryField[],
  fieldId: string | null | undefined,
): ObjectInventoryField | null => {
  if (!fieldId) return null
  return fields.find((field) => String(field.id) === fieldId) ?? null
}

/**
 * Возвращает id поля текущего паспорта, которое нужно менять из readonly-карточки.
 */
const resolveReadonlyFieldSourceId = (
  item: RuntimeFieldSlot,
  catalogTypeId: number,
  allFields: ObjectInventoryField[],
): string | null => {
  if (isElementLinkedFieldCell(item.layoutItem)) {
    const linkFieldId =
      item.layoutItem.link_field_id ?? parseLinkFieldIdFromElementId(String(item.layoutItem.id))
    return linkFieldId == null ? null : String(linkFieldId)
  }

  const gridId = String(item.layoutItem.id)
  if (gridId.startsWith('ref:')) {
    const segments = parseRefHopChainFromGridId(gridId)
    if (segments.length > 0) return String(segments[0].field_id)
  }

  const segments = parseHopSegmentsForLinkedResolution(
    item.layoutItem,
    catalogTypeId,
    allFields,
    item.field,
  )
  if (segments.length > 0) return String(segments[0].field_id)

  const leafId = parseLeafFieldIdForLinkedResolution(item.layoutItem, item.field)
  return leafId == null ? null : String(leafId)
}

/**
 * Собирает данные поля для модалки readonly-карточки основного грида.
 */
export const resolveReadonlyFieldModalTarget = (
  item: RuntimeFieldSlot,
  catalogTypeId: number,
  allFields: ObjectInventoryField[],
  fallbackLabel: string,
): RuntimeFieldModalTarget | null => {
  const fieldId = resolveReadonlyFieldSourceId(item, catalogTypeId, allFields)
  if (!fieldId) return null

  const field = findFieldById(allFields, fieldId) ?? item.field
  return {
    fieldId,
    field,
    label: field.name?.trim() || fallbackLabel,
  }
}

/**
 * Собирает данные собственного поля, которое отображается карточкой вместо поля ввода.
 */
export const resolveOwnFieldModalTarget = (
  item: RuntimeFieldSlot,
  label: string,
  allFields: ObjectInventoryField[] = [],
): RuntimeFieldModalTarget => {
  const field = findFieldById(allFields, item.fieldId) ?? item.field
  return {
    fieldId: item.fieldId,
    field,
    label: field?.name?.trim() || label,
  }
}

/**
 * Возвращает id редактируемого поля для unsupported/ref-карточки.
 */
const resolveUnsupportedModalFieldId = (
  item: RuntimePlaceholderItem,
  allFields: ObjectInventoryField[] = [],
): string | null => {
  const gridId = String(item.layoutItem.id)
  if (isElementLinkedFieldCell(item.layoutItem)) {
    const linkFieldId = item.layoutItem.link_field_id ?? parseLinkFieldIdFromElementId(gridId)
    return linkFieldId == null ? null : String(linkFieldId)
  }

  if (gridId.startsWith('ref:')) {
    const segments = parseRefHopChainFromGridId(gridId)
    if (segments.length > 0) return String(segments[0].field_id)
  }

  return focusFieldIdForPlaceholder(item, allFields)
}

/**
 * Находит метаданные поля для unsupported/ref-карточки с учётом составного id.
 */
const resolveUnsupportedFieldMeta = (
  item: RuntimePlaceholderItem,
  allFields: ObjectInventoryField[],
  sourceId = resolveUnsupportedModalFieldId(item, allFields),
): ObjectInventoryField | null => {
  const source = findFieldById(allFields, sourceId)
  if (source) return source

  const direct = getFieldMetaForPlaceholder(item, allFields)
  if (direct) return direct

  const fieldId = focusFieldIdForPlaceholder(item, allFields)
  return findFieldById(allFields, fieldId)
}

/**
 * Собирает данные поля для модалки unsupported/ref-карточки.
 */
export const resolveUnsupportedFieldModalTarget = (
  item: RuntimePlaceholderItem,
  allFields: ObjectInventoryField[],
): RuntimeFieldModalTarget | null => {
  const fieldId = resolveUnsupportedModalFieldId(item, allFields)
  const field = resolveUnsupportedFieldMeta(item, allFields, fieldId)
  if (!fieldId || !field) return null

  return {
    fieldId,
    field,
    label: item.layoutItem.name?.trim() || field.name,
  }
}
